import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Observable } from 'rxjs';
import { DevExDatePickerComponent } from '../../../devExtreme/src/devExDatePicker/devExDatePicker.component';
import { MatDatepickerComponent } from '../../../material/src/matDatepicker/matDatepicker.component';
import { ComponentStyle, IAbcComponent, StylingMode } from '../abcComponent.model';
import { AbcComponentService } from '../abcComponent.service';
import {
  DatePickerApplyValueMode,
  DatepickerStyle,
  DatePickerType,
  DateValidationMessageMode,
  DateValidationStatus,
  IDatepicker,
  PickerType,
} from './abcDatepicker';

@Component({
    selector: 'abcDatepicker',
    template: '<ng-template #abcDatepicker></ng-template>',
    standalone: false
})
export class AbcDatepickerComponent implements IAbcComponent, OnInit, AfterViewInit, OnDestroy {
  @Input('placeHolderLabel') attrPlaceholderLabel: string; // this can be a label or placeholder
  @Input('id') attrId: string;
  @Input('symbol') attrSymbol: string;
  @Input('decimalDigits') attrDecimalDigits: Number;
  @Input('isConverted') isConverted: boolean;
  @Input('useMaskBehavior') useMaskBehavior: boolean;
  @Output() newObj: string;
  @Output() onChange = new EventEmitter();
  @Output() valueChange = new EventEmitter();
  private _activeStateEnabled: boolean;
  private _adaptivityEnabled: boolean;
  private _applyButtonText: string;
  private _acceptCustomValue: boolean;
  private _applyValueMode: DatePickerApplyValueMode;
  private _cancelButtonText: string;
  private _validationMessageMode: DateValidationMessageMode;
  private _validationStatus: DateValidationStatus;
  private _opened: boolean;
  private _openOnFieldClick: boolean;
  private _pickerType: PickerType;
  private _readOnly: boolean;
  private _rtlEnabled: boolean;
  private _disabled: boolean;
  private _showAnalogClock: boolean;
  private _showClearButton: boolean;
  private _showDropDownButton: boolean;
  private _spellcheck: boolean;
  private _stylingMode: StylingMode;
  private _name: string;
  private _width: string;
  private _height: string;
  private _class: string;
  private _placeholder: string;
  private _style: ComponentStyle;
  private _visible: boolean;
  private _type: DatePickerType;
  private _min: Date | string;
  private _max: Date | string;
  private _value: any;
  private _valueChangeEvent: 'keyup' | 'blur' | 'change' | 'input';

  @Input()
  get style() {
    return this._style;
  }
  set style(data: ComponentStyle) {
    this._style = data;
    if (this.componentRef) {
      this.viewContainerRef.clear();
      this.createComponentInstance().subscribe((component) => {
        this.eventListener.forEach((element) => {
          component.addEventListener(element.type, element.listener);
        });
      });
    }
  }

  @Input()
  get height() {
    return this._height;
  }
  set height(data) {
    if (this.componentRef) {
      this.componentRef.height = data;
    }
    this._height = data;
  }

  @Input()
  get width() {
    return this._width;
  }
  set width(data) {
    if (this.componentRef) {
      this.componentRef.width = data;
    }
    this._width = data;
  }
  @Input()
  get min() {
    return this._min;
  }
  set min(data) {
    if (this.componentRef) {
      this.componentRef.min = data;
    }
    this._min = data;
  }
  @Input()
  get max() {
    return this._max;
  }
  set max(data) {
    if (this.componentRef) {
      this.componentRef.max = data;
    }
    this._max = data;
  }

  @Input()
  get visible() {
    return this._visible;
  }
  set visible(data) {
    if (this.componentRef) {
      this.componentRef.visible = data;
    }
    this._visible = data;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(data) {
    if (this.componentRef) {
      this.componentRef.placeholder = data;
    }
    this._placeholder = data;
  }

  @Input()
  get pickerType() {
    return this._pickerType;
  }
  set pickerType(data) {
    if (this.componentRef) {
      this.componentRef.pickerType = data;
    }
    this._pickerType = data;
  }

  @Input()
  get validationStatus() {
    return this._validationStatus;
  }
  set validationStatus(data) {
    if (this.componentRef) {
      this.componentRef.validationStatus = data;
    }
    this._validationStatus = data;
  }

  @Input()
  get validationMessageMode() {
    return this._validationMessageMode;
  }
  set validationMessageMode(data) {
    if (this.componentRef) {
      this.componentRef.validationMessageMode = data;
    }
    this._validationMessageMode = data;
  }

  @Input()
  get type() {
    return this._type;
  }
  set type(data) {
    if (this.componentRef) {
      this.componentRef.type = data;
    }
    this._type = data;
  }

  @Input()
  get cancelButtonText() {
    return this._cancelButtonText;
  }
  set cancelButtonText(data) {
    if (this.componentRef) {
      this.componentRef.cancelButtonText = data;
    }
    this._cancelButtonText = data;
  }

  @Input()
  get applyValueMode() {
    return this._applyValueMode;
  }
  set applyValueMode(data) {
    if (this.componentRef) {
      this.componentRef.applyValueMode = data;
    }
    this._applyValueMode = data;
  }

  @Input()
  get applyButtonText() {
    return this._applyButtonText;
  }
  set applyButtonText(data) {
    if (this.componentRef) {
      this.componentRef.applyButtonText = data;
    }
    this._applyButtonText = data;
  }

  @Input()
  get activeStateEnabled() {
    return this._activeStateEnabled;
  }
  set activeStateEnabled(data) {
    if (this.componentRef) {
      this.componentRef.activeStateEnabled = data;
    }
    this._activeStateEnabled = data;
  }

  @Input()
  get acceptCustomValue() {
    return this._acceptCustomValue;
  }
  set acceptCustomValue(data) {
    if (this.componentRef) {
      this.componentRef.acceptCustomValue = data;
    }
    this._acceptCustomValue = data;
  }

  @Input()
  get adaptivityEnabled() {
    return this._adaptivityEnabled;
  }
  set adaptivityEnabled(data) {
    if (this.componentRef) {
      this.componentRef.adaptivityEnabled = data;
    }
    this._adaptivityEnabled = data;
  }

  @Input()
  get class() {
    return this._class;
  }
  set class(data) {
    if (this.componentRef) {
      this.componentRef.class = data;
    }
    this._class = data;
  }

  @Input()
  get openOnFieldClick() {
    return this._openOnFieldClick;
  }
  set openOnFieldClick(data) {
    if (this.componentRef) {
      this.componentRef.openOnFieldClick = data;
    }
    this._openOnFieldClick = data;
  }

  @Input()
  get opened() {
    return this._opened;
  }
  set opened(data) {
    if (this.componentRef) {
      this.componentRef.opened = data;
    }
    this._opened = data;
  }

  @Input()
  get name() {
    return this._name;
  }
  set name(data) {
    if (this.componentRef) {
      this.componentRef.name = data;
    }
    this._name = data;
  }

  @Input()
  get stylingMode() {
    return this._stylingMode;
  }
  set stylingMode(data) {
    if (this.componentRef) {
      this.componentRef.stylingMode = data;
    }
    this._stylingMode = data;
  }

  @Input()
  get spellcheck() {
    return this._spellcheck;
  }
  set spellcheck(data) {
    if (this.componentRef) {
      this.componentRef.spellcheck = data;
    }
    this._spellcheck = data;
  }

  @Input()
  get showDropDownButton() {
    return this._showDropDownButton;
  }
  set showDropDownButton(data) {
    if (this.componentRef) {
      this.componentRef.showDropDownButton = data;
    }
    this._showDropDownButton = data;
  }

  @Input()
  get showClearButton() {
    return this._showClearButton;
  }
  set showClearButton(data) {
    if (this.componentRef) {
      this.componentRef.showClearButton = data;
    }
    this._showClearButton = data;
  }

  @Input()
  get showAnalogClock() {
    return this._showAnalogClock;
  }
  set showAnalogClock(data) {
    if (this.componentRef) {
      this.componentRef.showAnalogClock = data;
    }
    this._showAnalogClock = data;
  }

  @Input()
  get rtlEnabled() {
    return this._rtlEnabled;
  }
  set rtlEnabled(data) {
    if (this.componentRef) {
      this.componentRef.rtlEnabled = data;
    }
    this._rtlEnabled = data;
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(data) {
    if (this.componentRef) {
      this.componentRef.disabled = data;
    }
    this._disabled = data;
  }

  @Input()
  get readOnly() {
    return this._readOnly;
  }
  set readOnly(data) {
    if (this.componentRef) {
      this.componentRef.readOnly = data;
    }
    this._readOnly = data;
  }

  @Input()
  get value() {
    return this._value;
  }
  set value(data) {
    if (this.componentRef) {
      this.componentRef.value = data;
    }
    this._value = data;
  }

  @Input()
  get valueChangeEvent() {
    return this._valueChangeEvent;
  }
  set valueChangeEvent(data) {
    if (this.componentRef) {
      this.componentRef.valueChangeEvent = data;
    }
    this._valueChangeEvent = data;
  }

  @ViewChild('abcDatepicker', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  private componentRef;
  get id(): string {
    return this.componentRef.id;
  }
  htmlAttributes: IDatepicker;
  datepickerStyle: DatepickerStyle;
  eventListener = [];

  constructor(private abcComponentService: AbcComponentService) {
    this.style = ComponentStyle.DevExpress;
    this.rtlEnabled = this.abcComponentService.rtlEnabled;
  }

  ngOnInit() {
    this.setDatepickerAttributesIfAvailable();
    this.createComponentInstance().subscribe((component) => {
      this.eventListener.forEach((element) => {
        component.addEventListener(element.type, element.listener);
      });
    });
  }

  ngAfterViewInit() {
    this.abcComponentService.addInstance(this);
  }

  ngOnDestroy(): void {
    this.eventListener = [];
    this.abcComponentService.removeInstance(this);
  }

  createComponentInstance(): Observable<any> {
    const observable = new Observable((observer) => {
      // let componentRef: any;
      switch (this.style) {
        case ComponentStyle.Material: {
          this.componentRef = this.abcComponentService.loadComponent(
            MatDatepickerComponent,
            this.viewContainerRef
          ).componentRef.instance;
          //do specific stuff for Material
          break;
        }
        case ComponentStyle.DevExpress: {
          this.componentRef = this.abcComponentService.loadComponent(
            DevExDatePickerComponent,
            this.viewContainerRef
          ).componentRef.instance;
          //do specific stuff for DevExpress
          break;
        }
      }
      //do common stuff for all types
      if (this.htmlAttributes.id) this.componentRef.id = this.htmlAttributes.id;
      if (this.htmlAttributes.placeholder) this.componentRef.placeholder = this.htmlAttributes.placeholder;
      if (this.htmlAttributes.type) this.componentRef.type = this.htmlAttributes.type;
      if (this.htmlAttributes.value) this.componentRef.value = this.htmlAttributes.value;
      if (this.htmlAttributes.symbol) this.componentRef.symbol = this.htmlAttributes.symbol;
      if (this.htmlAttributes.decimalDigits) this.componentRef.decimalDigits = this.htmlAttributes.decimalDigits;
      if (this.htmlAttributes.class) this.componentRef.class = this.htmlAttributes.class;
      if (this.htmlAttributes.min) this.componentRef.min = this.htmlAttributes.min;
      if (this.htmlAttributes.max) this.componentRef.max = this.htmlAttributes.max;
      if (this.readOnly) this.componentRef.readOnly = this.readOnly;
      this.componentRef.rtlEnabled = this.rtlEnabled ? this.rtlEnabled : false;
      this.componentRef.disabled = this.htmlAttributes.disabled ? this.htmlAttributes.disabled : false;
      this.componentRef.showClearButton = this.htmlAttributes.showClearButton
        ? this.htmlAttributes.showClearButton
        : false;
      this.componentRef.valueChangeEvent = this.htmlAttributes.valueChangeEvent || 'change';
      this.componentRef.validationStatus = this.htmlAttributes.validationStatus
        ? this.htmlAttributes.validationStatus
        : 'valid';
      this.componentRef.stylingMode = this.stylingMode ? this.stylingMode : this.abcComponentService.stylingMode;
      this.componentRef.acceptCustomValue = this.acceptCustomValue;
      this.componentRef.applyValueMode = this.htmlAttributes.applyValueMode
        ? this.htmlAttributes.applyValueMode
        : 'instantly';
      this.componentRef.type = this.type ? this.type : this.htmlAttributes.type ? this.htmlAttributes.type : 'date';
      this.componentRef.min = this.min;
      this.componentRef.value = this.value;
      this.componentRef.isConverted = this.isConverted ? this.isConverted : false;
      this.componentRef.useMaskBehavior = this.useMaskBehavior ? this.useMaskBehavior : false;
      this.componentRef.setDatepickerStyle(this.htmlAttributes.setDatepickerStyle());
      this.componentRef.onChange.subscribe((event) => {
        this.dataChange(event);
      });
      this.componentRef.valueChange.subscribe((event) => {
        this._value = event;
        this.valueChange.emit(event);
      });

      observer.next(this.componentRef);
    });

    return observable;
  }

  setCustomDataAttributes(dataAttr: Object) {
    this.componentRef.setCustomDataAttributes(dataAttr);
  }

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ) {
    // I had to push events in an Array because this function is called before components are loaded for some reason
    this.eventListener.push({ type, listener });
  }

  // If the component is created inside HTML
  setDatepickerAttributesIfAvailable() {
    if (this.attrId) {
      const style: DatepickerStyle = {
        height: this.height ? this.height : 25,
        width: this.width ? this.width : '100%',
      };
      this.htmlAttributes = {
        id: this.attrId,
        class: this.class,
        type: this.type,
        placeholder: this.placeholder,
        activeStateEnabled: this.activeStateEnabled,
        adaptivityEnabled: this.adaptivityEnabled,
        acceptCustomValue: this.acceptCustomValue,
        applyValueMode: this.applyValueMode,
        applyButtonText: this.applyButtonText,
        cancelButtonText: this.cancelButtonText,
        validationMessageMode: this.validationMessageMode,
        validationStatus: this.validationStatus,
        opened: this.opened,
        openOnFieldClick: this.openOnFieldClick,
        pickerType: this.pickerType,
        readOnly: this.readOnly,
        rtlEnabled: this.rtlEnabled,
        disabled: this.disabled,
        value: this.value,
        showClearButton: this.showClearButton,
        showDropDownButton: this.showDropDownButton,
        stylingMode: this.stylingMode,
        visible: this.visible,
        min: this.min,
        max: this.max,
        isConverted: this.isConverted,
        useMaskBehavior: this.useMaskBehavior,
        valueChangeEvent: this.valueChangeEvent,
        setDatepickerStyle: (_datepickerStyle) => (_datepickerStyle = style),
        setCustomDataAttributes: () => {
          // To do...
        },
      };
    }
  }

  private dataChange(newValue) {
    this.onChange.emit({ newValue: newValue });
    this.value = newValue;
  }
  public getValidationStatus(): boolean {
    return this.componentRef.getValidationStatus();
  }
}
